import { Camera, RefreshCcw } from "lucide-react";
import { Html5Qrcode } from "html5-qrcode";
import { useEffect, useRef, useState } from "react";

export default function TicketScanner({
  onDecode,
}: {
  onDecode: (payload: string) => Promise<void>;
}) {
  const scannerId = "pulsepass-scanner";
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const hasDecodedRef = useRef(false);
  const [isActive, setIsActive] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isActive) return;

    const scanner = new Html5Qrcode(scannerId);
    scannerRef.current = scanner;
    hasDecodedRef.current = false;
    let mounted = true;

    Html5Qrcode.getCameras()
      .then((cameras) => {
        if (!mounted) return;

        if (!cameras.length) {
          setError("No camera was found on this device.");
          return;
        }

        return scanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 260, height: 260 } },
          async (decodedText) => {
            // html5-qrcode keeps firing this callback for every frame that
            // decodes the same QR (10 fps). Short-circuit duplicates and
            // stop the scanner BEFORE the network call so subsequent frames
            // can't trigger another /scan request.
            if (hasDecodedRef.current) return;
            hasDecodedRef.current = true;

            try {
              await scanner.stop();
            } catch {
              // Ignore — scanner may already be stopping.
            }

            try {
              await onDecode(decodedText);
            } finally {
              if (mounted) setIsActive(false);
            }
          },
          () => undefined
        );
      })
      .catch((scanError) => {
        setError(scanError instanceof Error ? scanError.message : "Unable to access the camera");
      });

    return () => {
      mounted = false;
      if (scannerRef.current?.isScanning) {
        void scannerRef.current.stop().catch(() => undefined);
      }
    };
  }, [isActive, onDecode]);

  return (
    <div className="ego-surface overflow-hidden">
      <div className="flex items-center justify-between border-b border-line px-5 py-4">
        <div>
          <div className="ego-eyebrow">QR scanner</div>
          <h3 className="mt-1 text-lg font-bold text-foreground">Live camera</h3>
        </div>
        <button
          type="button"
          onClick={() => {
            setError(null);
            setIsActive((value) => !value);
          }}
          className={isActive ? "ego-btn-secondary" : "ego-btn-primary"}
        >
          {isActive ? <RefreshCcw size={15} /> : <Camera size={15} />}
          {isActive ? "Stop" : "Start camera"}
        </button>
      </div>

      <div className="relative">
        <div
          id={scannerId}
          className="min-h-[400px] overflow-hidden bg-[#0b1220]"
        />
        {!isActive && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-[#0b1220] text-center">
            <div className="px-6">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-white/5 text-white/80 ring-1 ring-white/10">
                <Camera size={22} />
              </div>
              <div className="text-sm font-semibold text-white">Camera not active</div>
              <p className="mx-auto mt-1 max-w-xs text-xs text-white/60">
                Tap "Start camera" to begin scanning ticket QR codes.
              </p>
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="border-t border-line bg-[#fef2f2] dark:bg-[rgba(239,68,68,0.14)] px-5 py-3 text-sm font-semibold text-[#b91c1c]">
          {error}
        </div>
      )}
    </div>
  );
}
